import { withAITracking } from '@microsoft/applicationinsights-react-js';
import Button from '@mui/material/Button';
import {
	GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
	GridColDef,
	GridRowParams,
} from '@mui/x-data-grid-pro';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { unitStructureQuery } from 'api/queries';
import {
	DrivelineSubSystem,
	getChassisId,
} from 'api/responses/models/Unit/UnitStructure/DrivelineSubSystem';
import { getAllDrivelinesSorted } from 'domain/unit/Unit';
import {
	CustomDetailPanelToggle,
	getDetailPanelAutoHeight,
} from 'library/components/controls/datagrid/CustomDetailPanelToggle';
import { LocalizedDataGrid } from 'library/components/controls/datagrid/LocalizedDataGrid';
import { reactPlugin } from 'main/azureInsights/config';
import { ServiceProtocolsDataGrid } from 'modules/Drivelineview/Components/ServiceProtocol/ServiceProtocolsDataGrid';
import { ServiceButton } from 'modules/MaintenancePlanner/MaintenanceDataGrid/Components/ServiceButton';
import { useResolvedUnitIds } from 'modules/Shared/Components/Hooks/useNumberParam';

export const ServiceProtocolsPage = (): React.JSX.Element => {
	const { t } = useTranslation([
		'drivelineview',
		'common',
		'maintenancePlanner',
	]);
	const unitIds = useResolvedUnitIds();

	const { data } = useSuspenseQuery(unitStructureQuery(unitIds.id));

	const drivelineList = useMemo(
		() =>
			getAllDrivelinesSorted(data).filter(
				({ isServiceProtocolAvailable }) => isServiceProtocolAvailable
			),
		[data.installations.length, unitIds.id]
	);

	const hasEmob = drivelineList.filter(({ isEmob }) => isEmob).length > 0;

	const getDetailPanelContent = ({
		row: { id },
	}: GridRowParams<DrivelineSubSystem>) => (
		<ServiceProtocolsDataGrid
			drivelineId={id}
			isInnerGrid
			showEmobColumn={hasEmob}
			sx={(theme) => ({
				'& .MuiDataGrid-columnHeaders': {
					backgroundColor: theme.palette.background.default,
				},
				'& .MuiDataGrid-columnHeader': {
					backgroundColor: theme.palette.background.default,
				},
				'&.MuiDataGrid-root': {
					backgroundColor: theme.palette.background.default,
				},
			})}
		/>
	);

	const columns = useMemo((): GridColDef<DrivelineSubSystem>[] => {
		return [
			{
				...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
				renderCell: (params) => (
					<CustomDetailPanelToggle
						id={params.id}
						value={params.value}
					/>
				),
				display: 'flex',
			},
			{
				field: 'chassisId',
				headerName: t('common:driveline'),
				flex: 1,
				minWidth: 130,
				valueGetter: (_value, row) => getChassisId(row),
			},
			{
				field: 'actions',
				type: 'actions',
				align: 'right',
				flex: 0,
				minWidth: 300,
				cellClassName: 'service-protocol-grid__actions',
				getActions: ({ row }) => [
					<ServiceButton
						key={'generate'}
						ButtonElement={Button}
						buttonProps={{
							variant: 'outlined',
						}}
						buttonText={t(
							'maintenancePlanner:header-action-generate-protocol'
						)}
						chassisId={getChassisId(row)}
						id={row.id}
					/>,
				],
			},
		];
	}, [t]);

	return (
		<LocalizedDataGrid
			initialState={{
				density: 'compact', // Set default density to compact
			}}
			autoHeight
			columns={columns}
			rows={drivelineList}
			rowHeight={68}
			getDetailPanelHeight={getDetailPanelAutoHeight}
			getDetailPanelContent={getDetailPanelContent}
		/>
	);
};

export default withAITracking(
	reactPlugin,
	ServiceProtocolsPage,
	'UnitOverviewServiceProtocolsPage',
	'application-insight-container'
);
